const HEADER_ALIASES = {
  full_name: ['full_name', 'navn', 'name'],
  title: ['title', 'tittel'],
  company_name: ['company_name', 'selskap', 'company'],
  linkedin_url: ['linkedin_url', 'linkedin', 'url'],
  priority: ['priority', 'prioritet'],
  notes: ['notes', 'notater']
};

const normalizeHeader = (value) => value
  .replace(/^\uFEFF/, '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, '_');

export const normalizeImportLinkedInUrl = (value) => {
  if (typeof value !== 'string') return '';

  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
    const pathname = url.pathname.replace(/\/+$/, '').toLowerCase();
    return `https://${hostname}${pathname}`;
  } catch {
    return value.trim().replace(/\/+$/, '').toLowerCase();
  }
};

export const isImportLinkedInUrl = (value) => {
  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.toLowerCase();
    const pathname = url.pathname.replace(/\/+$/, '');

    return (
      url.protocol === 'https:' &&
      (hostname === 'linkedin.com' || hostname === 'www.linkedin.com') &&
      pathname.toLowerCase().startsWith('/in/') &&
      pathname.length > 4
    );
  } catch {
    return false;
  }
};

const nameFromUrl = (value) => {
  const slug = value.split('/in/')[1]?.split(/[/?#]/)[0] || '';
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const splitDelimitedLine = (line, delimiter) => {
  const values = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];

    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === delimiter && !quoted) {
      values.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }

  values.push(value.trim());
  return values;
};

const detectDelimiter = (line) => {
  const semicolonCount = (line.match(/;/g) || []).length;
  const commaCount = (line.match(/,/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
};

const mapRow = (values, headers) => {
  const row = {};

  headers.forEach((header, index) => {
    const key = Object.keys(HEADER_ALIASES).find((field) =>
      HEADER_ALIASES[field].includes(normalizeHeader(header))
    );

    if (key) row[key] = values[index] || '';
  });

  return row;
};

const hasHeader = (line) => {
  const normalized = line.toLowerCase();
  return ['linkedin_url', 'full_name', 'navn', 'name', 'linkedin'].some((key) =>
    normalized.includes(key)
  );
};

const parseLines = (content) => {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  const delimiter = detectDelimiter(lines[0]);
  const headerLine = hasHeader(lines[0]);
  const headers = headerLine
    ? splitDelimitedLine(lines[0], delimiter)
    : ['full_name', 'title', 'company_name', 'linkedin_url'];

  return lines
    .slice(headerLine ? 1 : 0)
    .map((line) => {
      if (!line.includes(delimiter)) {
        return { linkedin_url: line };
      }

      return mapRow(splitDelimitedLine(line, delimiter), headers);
    });
};

const toImportRow = (raw, index, existingUrls, importedUrls) => {
  const linkedinUrl = String(raw.linkedin_url || '').trim();
  const normalizedUrl = normalizeImportLinkedInUrl(linkedinUrl);
  const fullName = String(raw.full_name || '').trim();
  const resolvedName = fullName || nameFromUrl(linkedinUrl);
  const baseRow = {
    id: `import-${index}`,
    full_name: resolvedName,
    title: String(raw.title || '').trim(),
    company_name: String(raw.company_name || '').trim(),
    linkedin_url: linkedinUrl,
    priority: String(raw.priority || 'Medium').trim() || 'Medium',
    notes: String(raw.notes || '').trim(),
    normalizedUrl,
    status: 'Klar',
    error: ''
  };

  if (!linkedinUrl) {
    return { ...baseRow, status: 'Mangler LinkedIn URL', error: 'LinkedIn URL mangler.' };
  }

  if (!isImportLinkedInUrl(linkedinUrl)) {
    const isSearchUrl = linkedinUrl.toLowerCase().includes('linkedin.com/search/results/people');

    return {
      ...baseRow,
      status: 'Ugyldig LinkedIn URL',
      error: isSearchUrl
        ? 'Lim inn profil-URL, ikke søke-URL.'
        : 'Ugyldig LinkedIn-profil-URL.'
    };
  }

  if (!resolvedName) {
    return { ...baseRow, status: 'Mangler navn', error: 'Navn mangler.' };
  }

  if (existingUrls.has(normalizedUrl) || importedUrls.has(normalizedUrl)) {
    return { ...baseRow, status: 'Duplikat', error: 'Prospektet finnes allerede.' };
  }

  importedUrls.add(normalizedUrl);
  return baseRow;
};

export const parseLinkedInProspects = (content, existingProspects = []) => {
  const existingUrls = new Set(
    existingProspects
      .map((prospect) => normalizeImportLinkedInUrl(prospect.linkedin_url))
      .filter(Boolean)
  );
  const importedUrls = new Set();

  return parseLines(content).map((row, index) =>
    toImportRow(row, index, existingUrls, importedUrls)
  );
};

export const parseLinkedInCsvFile = (file, existingProspects = []) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = (event) => {
    try {
      resolve(parseLinkedInProspects(event.target.result || '', existingProspects));
    } catch (error) {
      reject(error);
    }
  };

  reader.onerror = () => reject(new Error('CSV-filen kunne ikke leses.'));
  reader.readAsText(file);
});